/**
 * InboxScreen — unified notification center with 4 tabs.
 * All | Direct | Comments | Notifications
 */

import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@turnifly/shared/stores'
import { InboxHeader } from './inbox-header'
import { InboxTabs } from './inbox-tabs'
import { InboxAllList } from './inbox-all-list'
import { InboxDirectList } from './inbox-direct-list'
import { NotificationRow } from './notification-row'
import {
  useNotifications,
  useMarkAllRead,
  useCombinedUnreadCount,
} from '@turnifly/shared/hooks'
import { useConversations } from '@turnifly/shared/hooks'
import { LoadingSpinner } from '@turnifly/shared/ui'
import type { InboxTab } from './inbox-tabs'

export const InboxScreen: React.FC = () => {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const [activeTab, setActiveTab] = useState<InboxTab>('all')

  const { data: unread } = useCombinedUnreadCount(user?.id, 30_000)
  const { data: conversations } = useConversations(user?.id)
  const { data: commentNotifs, isLoading: loadingComments } = useNotifications(
    user?.id,
    activeTab === 'comments' ? 'comment' : undefined,
  )
  const { data: sysNotifs, isLoading: loadingSys } = useNotifications(
    user?.id,
    activeTab === 'notifications' ? undefined : undefined,
  )
  const markAllRead = useMarkAllRead(user?.id)

  // Count unread DMs
  const unreadDMs = useMemo(() =>
    (conversations ?? []).reduce((sum, c) => sum + c.unreadCount, 0),
  [conversations])

  const tabs = useMemo(() => [
    { id: 'all' as InboxTab, label: t('inbox.all'), count: unread?.total },
    { id: 'direct' as InboxTab, label: t('inbox.direct'), count: unreadDMs },
    { id: 'comments' as InboxTab, label: t('inbox.comments') },
    { id: 'notifications' as InboxTab, label: t('inbox.notifications'), count: unread?.notifications },
  ], [t, unread, unreadDMs])

  return (
    <div className="h-[100dvh] flex flex-col bg-bg">
      <InboxHeader
        onMarkAllRead={() => markAllRead.mutate()}
        isMarkingRead={markAllRead.isPending}
      />

      <InboxTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

      <div className="flex-1 overflow-y-auto">
        {activeTab === 'all' && <InboxAllList />}

        {activeTab === 'direct' && <InboxDirectList />}

        {activeTab === 'comments' && (
          <NotificationListByType
            notifications={commentNotifs}
            isLoading={loadingComments}
            emptyMessage={t('inbox.noComments')}
          />
        )}

        {activeTab === 'notifications' && (
          <NotificationListByType
            notifications={sysNotifs}
            isLoading={loadingSys}
            emptyMessage={t('inbox.noNotifications')}
          />
        )}
      </div>
    </div>
  )
}

// ── Helper component for notification-only tabs ─────────────

import type { NotificationItem } from '@turnifly/shared/api'

interface NotificationListByTypeProps {
  notifications: NotificationItem[] | undefined
  isLoading: boolean
  emptyMessage: string
}

const NotificationListByType: React.FC<NotificationListByTypeProps> = ({
  notifications, isLoading, emptyMessage,
}) => {
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="md" />
      </div>
    )
  }

  if (!notifications?.length) {
    return (
      <div className="py-12 text-center text-text-hint text-sm">
        {emptyMessage}
      </div>
    )
  }

  return (
    <div>
      {notifications.map((n) => (
        <NotificationRow key={n.id} notification={n} />
      ))}
    </div>
  )
}
